import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../../../js/authToken';
import API_BASE_URL from '../../../../js/urlHelper';

import ClienteAvalForm from './ClienteAvalForm';
import AsignacionesTable from './AsignacionesTable';

const ClienteAvales = () => {
  const [clientes, setClientes] = useState([]);
  const [avales, setAvales] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [filteredAsignaciones, setFilteredAsignaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [clientesResponse, avalesResponse, asignacionesResponse] = await Promise.all([
          fetchWithAuth(`${API_BASE_URL}/api/admin/asignacion/getclientes`),
          fetchWithAuth(`${API_BASE_URL}/api/admin/asignacion/getavales`),
          fetchWithAuth(`${API_BASE_URL}/api/admin/asignacion/clienteavales`),
        ]);

        const [clientesData, avalesData, asignacionesData] = await Promise.all([
          clientesResponse.json(),
          avalesResponse.json(),
          asignacionesResponse.json(),
        ]);

        setClientes(clientesData);
        setAvales(avalesData);
        setAsignaciones(asignacionesData);
        setFilteredAsignaciones(asignacionesData);
      } catch (error) {
        setError('Error al cargar datos. Intente nuevamente.');
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAsignarAval = async (idCliente, idAval) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/asignacion/clienteavales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idCliente,
          idAval,
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        // Actualizar lista de asignaciones
        const asignacionesResponse = await fetchWithAuth(`${API_BASE_URL}/api/admin/asignacion/clienteavales`);
        const asignacionesData = await asignacionesResponse.json();
        setAsignaciones(asignacionesData);
        setFilteredAsignaciones(asignacionesData);
        
        setSuccess('Asignación realizada correctamente');
        setError('');
        return true;
      } else {
        setError(data.message || 'Error al realizar la asignación');
        return false;
      }
    } catch (error) {
      setError('Error en el servidor. Intente nuevamente.');
      console.error('Error:', error);
      return false;
    }
  };

  const handleEliminarAsignacion = async (id) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/asignacion/clienteavales/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Actualizar lista de asignaciones
        const updatedAsignaciones = asignaciones.filter(asignacion => asignacion.id !== id);
        setAsignaciones(updatedAsignaciones);
        setFilteredAsignaciones(updatedAsignaciones);
        setSuccess('Asignación eliminada correctamente');
      } else {
        setError('Error al eliminar la asignación');
      }
    } catch (error) {
      setError('Error en el servidor. Intente nuevamente.');
      console.error('Error:', error);
    }
  };

  const handleFilterAsignaciones = (searchTerm) => {
    if (!searchTerm) {
      setFilteredAsignaciones(asignaciones);
      return;
    }
    
    const filteredResults = asignaciones.filter(asignacion => {
      const clienteNombre = asignacion.cliente?.nombreCompleto?.toLowerCase() || '';
      const avalNombre = asignacion.aval?.nombreCompleto?.toLowerCase() || '';
      
      return clienteNombre.includes(searchTerm.toLowerCase()) || 
             avalNombre.includes(searchTerm.toLowerCase());
    });
    
    setFilteredAsignaciones(filteredResults);
  };

  const avalesAsignadosACliente = (clienteId) => {
    return asignaciones.filter(asignacion => asignacion.idCliente === parseInt(clienteId, 10)).length;
  };

  const clienteReachedLimit = (clienteId) => {
    return avalesAsignadosACliente(clienteId) >= 2;
  };

  const clienteTieneAvalAsignado = (clienteId, avalId) => {
    return asignaciones.some(
      (asignacion) =>
        asignacion.idCliente === parseInt(clienteId, 10) &&
        asignacion.idAval === parseInt(avalId, 10)
    );
  };

  const clientesConAval = (avalId) => {
    return asignaciones.filter(asignacion => asignacion.idAval === parseInt(avalId, 10)).length;
  };

  return (
    <div className="space-y-6">
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_90%_12%,rgba(217,44,52,0.15),transparent_40%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-600">Gestión de clientes</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Asignación de avales</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Vincula clientes con avales disponibles. Cada cliente puede tener hasta 2 avales y cada aval puede
            respaldar hasta 2 clientes.
          </p>
        </div>
      </section>

      {!!error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!!success && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {success}
        </div>
      )}

      <section className="ui-card p-6">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Nueva vinculación</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-900">Selecciona cliente y aval</h2>
        </div>
        <ClienteAvalForm
          clientes={clientes}
          avales={avales}
          avalesAsignadosACliente={avalesAsignadosACliente}
          clienteReachedLimit={clienteReachedLimit}
          clienteTieneAvalAsignado={clienteTieneAvalAsignado}
          clientesConAval={clientesConAval}
          onAsignarAval={handleAsignarAval}
          loading={loading}
        />
      </section>

      <section className="ui-card p-6">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Historial</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-900">Asignaciones actuales</h2>
        </div>
        <AsignacionesTable
          asignaciones={filteredAsignaciones}
          loading={loading}
          onFilterAsignaciones={handleFilterAsignaciones}
          onEliminarAsignacion={handleEliminarAsignacion}
        />
      </section>
    </div>
  );
};

export default ClienteAvales;
